import React, { useState } from 'react';
import { Alert, Tooltip } from '@mui/material';
import { Button, Icon } from '@vacasa/react-components-lib';
import { encode } from 'base64-arraybuffer';
import { useUploadPropertyManagerConfigMutation } from '../../services';
import { UnitMigrationRollbackModal } from './UnitMigrationRollbackModal';
import './PropertyManagerConfig.scss';

export const PropertyManagerConfig: React.FC = () => {
    const [fileName, setFileName] = useState('');
    const [fileContent, setFileContent] = useState('');
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const [uploadPropertyManagerConfig, { isLoading }] = useUploadPropertyManagerConfigMutation();

    const [rollbackModalOpen, setRollbackModalOpen] = useState(false);

    const onSelectFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }
        const supportedTypes = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
        if (!supportedTypes.includes(file.type) && !file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
            setIsError(true);
            setMessage('Unsupported format. Upload a .csv or .xlsx file.');
            return;
        }
        const buffer = await file.arrayBuffer();
        setFileName(file.name);
        setFileContent(encode(buffer));
        setIsError(false);
        setMessage('');
    };

    const onUpload = async () => {
        try {
            setIsError(false);
            setMessage('');
            const result = await uploadPropertyManagerConfig({
                file: fileContent,
                file_name: fileName,
            }).unwrap();
            setMessage(
                `Upload queued. Total: ${result.queued}, pm-config: ${result.pm_config}, unit-config-override: ${result.unit_config_override}, skipped: ${result.skipped}.`
            );
        } catch (_e) {
            setIsError(true);
            setMessage('Unable to upload Property Manager Config. Please validate the file and try again.');
        }
    };

    const onBrowse = () => {
        const input = document.getElementById('property-manager-config-input');
        input?.click();
    };

    return (
        <div className="property-manager-config-main">
            <div className="property-manager-config-header">
                <div className="property-manager-config-header-row">
                    <div className="property-manager-config-header-text">
                        <h3>Property Manager Config</h3>
                        <p>Upload a CSV/XLSX file to configure your property manager settings and unit-level preferences.</p>
                    </div>
                    <div className="property-manager-config-header-toolbar">
                        <Tooltip title="Rollback" placement="bottom" arrow>
                            <button
                                type="button"
                                className="property-manager-config-rollback-fab"
                                onClick={() => setRollbackModalOpen(true)}
                                aria-label="Unit migration rollback"
                            >
                                <Icon.RotateCCW width={20} height={20} aria-hidden />
                            </button>
                        </Tooltip>
                    </div>
                </div>
            </div>
            {message && <Alert severity={isError ? 'error' : 'success'}>{message}</Alert>}
            <div className="property-manager-config-body">
                <div className="property-manager-config-input">
                    <button className="property-manager-config-browse-button" onClick={onBrowse}>
                        <Icon.Monitor />
                        <label>Browse CSV/XLSX file</label>
                    </button>
                    <input
                        id="property-manager-config-input"
                        className="property-manager-config-file-input"
                        type="file"
                        accept=".csv,.xlsx"
                        onChange={onSelectFile}
                    />
                </div>
                <div className="property-manager-config-submit">
                    <Button variant="secondary" onClick={onUpload} disabled={!fileContent || isLoading}>
                        Upload
                    </Button>
                </div>
            </div>
            {fileName && <div className="property-manager-config-file">Selected file: {fileName}</div>}

            <UnitMigrationRollbackModal open={rollbackModalOpen} onClose={() => setRollbackModalOpen(false)} />
        </div>
    );
};
